import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, from, map, of, shareReplay, switchMap } from 'rxjs';
import {
  AccessModules,
  CompanyResponse,
  MyUser,
  PermissionsForRequisitions,
  Token,
  TokenResponse,
  UserResponse
} from '../shared/models/user.model';
// import { baseURL } from './config';
import { environment } from '../../../environments/environment';
import { generateUUID, handleApiResponse } from '../utils/common-methods';
import { AlertMessageService } from './alert-message.service';
import { UserPermissionsService } from './user-permissions.service';
import { UserBackendAdapter, UserModel } from "../features/project-management/models/user.model";
import { Router } from '@angular/router';
import { collection, collectionChanges, collectionData, Firestore, onSnapshot } from '@angular/fire/firestore';
import mixpanel from 'mixpanel-browser';
import { IdentityProfile, IdentityService } from './identity.service';

/** Resultado del intercambio del ID token de Firebase por el token de obra-backend. */
export interface SessionExchangeResult {
  token?: string;
  errorMessage?: string;
}

const SESSION_STORAGE_KEYS = ['token_data', 'token_id', 'user_data'];



@Injectable({
  providedIn: 'root',
})
export class UserService {
  public headers = new HttpHeaders();
  private tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private auxUser = new UserBackendAdapter();
  private isOnMultipleCheck = false;

  private user: IdentityProfile | null | undefined;
  private exchangeInFlight?: Observable<SessionExchangeResult>;
  public userMy?: MyUser;

  constructor(
    private http: HttpClient,
    private userPermissionsService: UserPermissionsService,
    private alertService: AlertMessageService,
    private identityService: IdentityService,
    private router: Router,
    private firestore: Firestore
  ) {
    this.tokenSubject.subscribe((token: string) => {
      if (token && token.trim().length > 0) {
        this.headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
      } else {
        this.headers = new HttpHeaders();
      }
    });
  }

  async onDetectMultipleAuthSession() {

    if (!environment.singleSessionCheck) return;

    if (!this.isOnMultipleCheck) {
      const company = localStorage.getItem('companyName');
      const uuid = localStorage.getItem("uuid");
      const user = this.user ?? this.getAuthUserData();

      if(!user) {
        this.isOnMultipleCheck = true;
        return;
      }

      const unsuscribeSession = onSnapshot(collection(this.firestore, `/dw/sessions/${company!.toLocaleLowerCase()}/${user!.email!.toLocaleLowerCase()}/active-sessions`), (querySnapshot) => {
        if (querySnapshot.empty) return;
        let destroy = true;
        for (const doc of querySnapshot.docs) {
          if (doc.id == uuid) {
            destroy = false;
            break;
          }

        }

        if (destroy) {
          unsuscribeSession();
          this.logout();
        }
      });
      this.isOnMultipleCheck = true;
    }
  }

  public getAccessModules(): Observable<AccessModules> {
    const apiURL = `${environment.baseURL}/users/my`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: UserResponse) => {
        return (handleApiResponse(response, this.alertService) as MyUser).user.accessModules as AccessModules ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching user data:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({});
      })
    );
  }

  public getUserPermissionsInRequisitions(): Observable<void> {
    const apiURL = `${environment.baseURL}/requisition_users/permission`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: UserResponse) => {
        const res = handleApiResponse(response, this.alertService)
        this.userPermissionsService.setPermissions(res as PermissionsForRequisitions);
      })
    );
  }

  setTokenSubject(token: string) {
    this.tokenSubject.next(token);
  }

  getAuthUserData() {
    const user = localStorage.getItem("user_data")
    if(user) {
      const userData = JSON.parse(user);
      mixpanel.identify(userData.email ?? 'no-identificable');
      return userData as IdentityProfile;
    }

    return null;
  }

  hasTokenValid() {
    const previousToken = localStorage.getItem("token_data");
    const userdata =  this.getAuthUserData();
    if (previousToken && userdata) {
      const tokenData = JSON.parse(previousToken);
      if (tokenData.expiration) {
        const expirationDate = new Date(tokenData.expiration);
        const currentDate = new Date();
        if (expirationDate > currentDate) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Inicia la sesión de dcw para el usuario de Firebase y la empresa indicada:
   * guarda empresa y perfil, e intercambia el ID token por el token de obra-backend.
   */
  public establishSession(profile: IdentityProfile, company: string): Observable<SessionExchangeResult> {
    const companyName = company.trim().toLowerCase();
    localStorage.setItem('companyName', companyName);
    localStorage.setItem('user_data', JSON.stringify(profile));
    if (!localStorage.getItem('uuid')) {
      localStorage.setItem('uuid', generateUUID());
    }
    this.user = profile;

    return this.exchangeFirebaseToken().pipe(
      map((result) => {
        if (result.token) {
          mixpanel.identify(profile.email || 'no-identificable');
          mixpanel.track('User Logged In', {
            user_email: profile.email || 'no-identificable',
            company: companyName,
            login_method: profile.sign_in_provider ?? 'password',
            environment: environment.environment
          });
        }
        return result;
      })
    );
  }

  /**
   * Obtiene un token vigente de obra-backend a partir de la sesión Firebase actual.
   * Las llamadas simultáneas comparten el mismo intercambio.
   */
  public exchangeFirebaseToken(): Observable<SessionExchangeResult> {
    if (this.exchangeInFlight) return this.exchangeInFlight;

    const company = localStorage.getItem('companyName') ?? '';
    const apiURL = `${environment.baseURL}/validate/firebase`;

    this.exchangeInFlight = from(this.identityService.getIdToken()).pipe(
      switchMap((idToken) => {
        if (!idToken || !company) {
          return of<SessionExchangeResult>({ errorMessage: 'Tu sesión terminó. Inicia sesión nuevamente.' });
        }
        return this.http.post<TokenResponse>(apiURL, { company, id_token: idToken }).pipe(
          map((response): SessionExchangeResult => {
            if (response.error?.['has-error'] || !response.response?.token) {
              return { errorMessage: response.error?.error?.message?.trim() || 'No fue posible iniciar la sesión en esta empresa.' };
            }
            const token = response.response.token;
            const expiresIn = response.response.expires_in ?? 3600;
            // Renueva un minuto antes del vencimiento real del JWT.
            const expiration = Date.now() + Math.max(expiresIn - 60, 60) * 1000;
            localStorage.setItem('token_data', JSON.stringify({ token, token_type: 'Bearer', expiration }));
            this.tokenSubject.next(token);
            this.onDetectMultipleAuthSession();
            return { token };
          })
        );
      }),
      catchError(() => of<SessionExchangeResult>({ errorMessage: 'No fue posible comunicarse con el servidor. Intenta nuevamente.' })),
      finalize(() => (this.exchangeInFlight = undefined)),
      shareReplay(1)
    );
    return this.exchangeInFlight;
  }

  /** Token vigente guardado, o null si no existe o ya venció. */
  public storedToken(): string | null {
    if (!this.hasTokenValid()) return null;
    const tokenData = JSON.parse(localStorage.getItem('token_data')!);
    return tokenData.token ?? null;
  }

  /** Cierra la sesión local y la de Firebase; conserva la última empresa para el siguiente acceso. */
  public async logout(): Promise<void> {
    SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    this.tokenSubject.next('');
    this.user = null;
    this.userPermissionsService.logout();
    try {
      await this.identityService.signOut();
    } finally {
      this.router.navigate(['/login']);
    }
  }

  getDataUser(): Observable<MyUser> {
    const apiURL = `${environment.baseURL}/users/my`;
    return this.http.get<UserResponse>(apiURL, { headers: this.headers }).pipe(
      map((response: UserResponse) => {
        this.userMy = handleApiResponse(response, this.alertService) as MyUser || null;
        return this.userMy ?? {};
      })
    );
  }

  public validateCompany(company: string): Observable<boolean> {
    const apiURL = `${environment.baseURL}/company/validate/${company}`;
    return this.http.get<CompanyResponse>(apiURL).pipe(
      map((response: CompanyResponse) => {
        const res = handleApiResponse(response, this.alertService)
        if (!response.error['has-error']) {
          return true;
        } else {
          return false;
        }
      })
    );
  }

  public getUsers(context?: string, project?: number): Observable<UserModel[]> {
    let apiURL = `${environment.baseURL}/users`;
    if (context) {
      apiURL += `?context=${context}`;
    }
    if (project) {
      apiURL += `&project=${project}`;
    }

    return this.http.get<UserResponse>(apiURL, { headers: this.headers })
      .pipe(
        map((response: UserResponse) => {
          return (handleApiResponse(response, this.alertService) as any).map((u: any) => {
            return this.auxUser.adapt(u);
          })
        }),
        catchError((error) => {
          console.error('Error fetching user data:', error);
          this.alertService.updateAlert(error.error!);
          this.alertService.changeErrorState(true);
          return of([]);
        })
      );
  }
}
